import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";

const { Option } = Select;

const REGIONS = [
  "America",
  "Canada",
  "UK",
  "India",
  "UAE",
  "Germany",
  "Australia",
  "New Zealand",
  "Nigeria",
  "Israel",
  "France",
  "Singapore",
  "Netherlands",
  "Belgium",
  "Spain",
  "Philippines",
  "Italy",
];
const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["bold", "italic", "underline", "list", "bullet"];

export default function CreateRequirementModal({
  open,
  onCancel,
  onSubmit,
  users,
  currentUser,
  submitting,
}) {
  const [form] = Form.useForm();
  const [description, setDescription] = useState("");

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit({ ...values, description }); // passes raw form values; hook sends _id for assignedTo
        form.resetFields();
        setDescription("");
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <span style={{ color: "#13487D", fontWeight: 700, fontSize: 17 }}>
          New Requirement
        </span>
      }
      open={open}
      onCancel={handleCancel}
      width={620}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          loading={submitting}
          style={{ background: "#3699FF", borderColor: "#3699FF" }}
        >
          Create Requirement
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ assignedTo: currentUser?._id }}
        style={{ marginTop: 8 }}
      >
        {/* Job Name */}
        <Form.Item
          name="jobName"
          label="Job Name"
          rules={[{ required: true, message: "Please enter the job name" }]}
        >
          <Input placeholder="e.g. Senior React Developer" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="region"
              label="Region"
              rules={[{ required: true, message: "Please select a region" }]}
            >
              <Select placeholder="Select region">
                {REGIONS.map((r) => (
                  <Option key={r} value={r}>
                    {r}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="duration"
              label="Duration"
              rules={[{ required: true, message: "Please enter duration" }]}
            >
              <Input placeholder="e.g. 6 months" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="projectValue"
              label="Project Value (USD)"
              rules={[
                { required: true, message: "Please enter project value" },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={(v) =>
                  `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                placeholder="45000"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="hourlyRate" label="Hourly Rate (USD) — Optional">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={(v) => (v ? `$ ${v}` : "")}
                parser={(v) => v.replace(/\$\s?/g, "")}
                placeholder="e.g. 85"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Created By — read only display */}
          <Col span={12}>
            <Form.Item label="Created By">
              <Input value={currentUser?.name || ""} disabled />
            </Form.Item>
          </Col>

          {/* Assigned To — sends _id to backend */}
          <Col span={12}>
            <Form.Item
              name="assignedTo"
              label="Assigned To"
              rules={[{ required: true, message: "Please assign to someone" }]}
            >
              <Select
                placeholder="Select person"
                showSearch
                optionFilterProp="children"
              >
                {/* Self first */}
                {currentUser?._id && (
                  <Option key={currentUser._id} value={currentUser._id}>
                    {currentUser.name} (me)
                  </Option>
                )}
                {users
                  .filter((u) => u._id !== currentUser?._id)
                  .map((u) => (
                    <Option key={u._id} value={u._id}>
                      {u.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Description — full width, optional */}
        <Form.Item
          label={
            <span>
              Description{" "}
              <span style={{ color: "#BAC7D6", fontWeight: 400, fontSize: 12 }}>
                — Optional
              </span>
            </span>
          }
        >
          <div className="modal-quill-wrapper">
            <ReactQuill
              theme="snow"
              value={description}
              onChange={setDescription}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Add context, skills required, client notes..."
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
